import { makeAutoObservable } from 'mobx';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import { TRANSPORTS, STATUS } from '../../utils/constants';
import moment from 'moment';
import { defaultZero } from '../../utils/helpers';
import { palette } from '@awarego-ui/components';

const statuses = {
    [STATUS.DRAFT]: {
        label: 'Draft',
        icon: false,
        value: STATUS.DRAFT,
        color: palette.graphite.heavyMetal,
    },
    [STATUS.SCHEDULED]: {
        label: 'Scheduled',
        icon: false,
        value: STATUS.SCHEDULED,
        color: palette.vibrant.blue,
    },
    [STATUS.ACTIVE]: {
        label: 'Active',
        icon: false,
        value: STATUS.ACTIVE,
        color: palette.vibrant.orange,
    },
    [STATUS.COMPLETED]: {
        label: '✓ Completed',
        icon: true,
        value: STATUS.COMPLETED,
        color: palette.evergreen.aspargus,
    },
};

class Assessment {
    selectedQuestionsIds = [];
    selectedUserListsIds = [];
    date = null;
    name = '';
    threatAreas = [];
    scheduleType = 1;
    channel = null;
    active = false;
    enabledLanguages = [];
    introMessageEnabled = false;
    due = null;
    id = null;
    templateCustomized = false;
    reminders_enabled = false;
    emails_enabled = false;
    customization_enabled = false;
    emailsToNotify = [];
    reminders_channel = null;
    reminders_steps = [{ id: 0, send: null }];
    remindersMode = null;
    remindersRrule = null;
    remindersDtstart = null;
    remindersExcluded = [];
    status = null;
    companyName = '';
    companyDomain = '';
    emailHR = '';
    userListSearchValue = '';
    isScorm = false;
    nameTranslations = {};
    overrideDuration = null;
    isAssessmentInsight = false;
    authentication = null;
    bannerImageSrc = '';
    bannerLinkURL = '';
    finishUrl = '';
    type = 'default';
    tagColumn = '';
    uniqKey = null;
    disableSyncLists = false;
    nextReminderId = 1;

     constructor(listStore, authStore, mainStore, questionsIndex, assessmentData, companyId, enabledLanguages, isAssessmentInsight) {
        makeAutoObservable(this);

        this.listStore = listStore;
        this.authStore = authStore;
        this.mainStore = mainStore;
        this.companyId = companyId;
        this.questionsIndex = questionsIndex;
        this.enabledLanguages = enabledLanguages;
        this.isAssessmentInsight = isAssessmentInsight;

        this.loadFromLocalStorage(companyId);

        if (assessmentData) {
            this.loadFromAssessmentData(assessmentData);
        }

        if (this.isAssessmentInsight) {
            this.type = 'hraInsight';
            this.scheduleType = -1;
        }

        if (!this.name) {
            this.name = this.isAssessmentInsight ? 'Untitled Insight' : 'Untitled Assessment';
        }

        if (!this.authentication && this.isAssessmentInsight) {
            this.authentication = 'required-email';
        }

        if (!assessmentData) {
            const settings = this.mainStore.currentCompanyUISettings || {};
            this.bannerImageSrc = settings['ui.minihra_banner_image_src'] || '';
            this.bannerLinkURL = settings['ui.minihra_banner_link_url'] || '';
            this.finishUrl = settings['ui.minihra_finish_url'] || '';
            if (this.bannerImageSrc || this.bannerLinkURL || this.finishUrl) {
                this.customization_enabled = true;
            }
        }
    }

    async loadFromLocalStorage(companyId) {
        this.channel = window.localStorage.getItem(`assessmentChannel-${companyId}`) || TRANSPORTS.EMAIL;
        this.reminders_channel = window.localStorage.getItem(`assessmentRemindersChannel-${companyId}`) || TRANSPORTS.EMAIL;
        this.companyName = window.localStorage.getItem(`companyName-${companyId}`) || '';
        this.companyDomain = window.localStorage.getItem(`companyDomain-${companyId}`) || '';
        this.emailHR = window.localStorage.getItem(`emailHR-${companyId}`) || '';
    }

    loadFromAssessmentData(data) {
        this._rawData = data.id ? data : null;
        this.name = data.name;
        this.id = data.id;
        this.nameTranslations =
            data.nameTranslations ||
            (data.definition && data.definition.nameTranslations) ||
            {};
        this.date = data.sendOptions?.date || null;
        this.reminders_enabled = !!data.reminders_enabled;
        this.active = data.active === 1;
        this.due = data.due;
        this.status = data.status;
        this.generalAssessmentStatus = data.generalAssessmentStatus;
        this.scheduleType = data.sendOptions?.scheduleType ?? this.scheduleType;
        this.channel = data.sendOptions?.channel || this.channel;
        this.reminders_channel = data.reminders_channel || this.reminders_channel;
        this.remindersMode = data.remindersMode || data.definition?.remindersMode || (data.reminders_steps?.length ? 'fixed' : null);
        this.remindersRrule = data.remindersRrule || data.definition?.remindersRrule || null;
        this.remindersDtstart = data.remindersDtstart || data.definition?.remindersDtstart || null;
        this.remindersExcluded = Array.isArray(data.remindersExcluded)
            ? data.remindersExcluded
            : Array.isArray(data.definition?.remindersExcluded)
              ? data.definition.remindersExcluded
              : [];

        if (data.definition?.questions) {
            this.selectedQuestionsIds.replace(data.definition.questions);
        }

        if (data.definition?.userLists) {
            this.selectedUserListsIds.replace(data.definition.userLists);
        }

        if (data.definition?.personalizeData) {
            const { companyName, companyDomain, emailHR } = data.definition.personalizeData;
            this.companyName = companyName;
            this.companyDomain = companyDomain;
            this.emailHR = emailHR;
        }

        if (data.definition?.emailsToNotify) {
            this.emailsToNotify = data.definition.emailsToNotify;
        }

        if(data.definition?.emails_enabled) {
            this.emails_enabled = data.definition.emails_enabled;
        }

        if (data.definition?.overrideDuration) {
            this.overrideDuration = data.definition.overrideDuration;
        }

        if (data.definition?.authentication) {
            this.authentication = data.definition.authentication;
        }

        if (data.definition?.bannerImageSrc) {
            this.bannerImageSrc = data.definition.bannerImageSrc;
        }

        if (data.definition?.bannerLinkURL) {
            this.bannerLinkURL = data.definition.bannerLinkURL;
        }

        if (data.definition?.finishUrl) {
            this.finishUrl = data.definition.finishUrl;
        }

        if (data.definition?.customization_enabled) {
            this.customization_enabled = data.definition.customization_enabled;
        }

        if (data.definition?.availableLanguages) {
            this.enabledLanguages = data.definition.availableLanguages;
        }

        if (data.reminders_steps && data.reminders_steps.length > 0) {
            this.reminders_steps.replace(
                data.reminders_steps.map(r => ({
                    ...r,
                    id: r.id ?? this.nextReminderId++,
                    alreadySet: true,
                }))
            );
        }

        this.threatAreas = data.areas;

        if (data.templateIds) {
            this.templateCustomized = data.template_customized;
        }

        if(data.isScorm){
            this.isScorm = data.isScorm;
        }
        this.isAssessmentInsight = data.definition?.type === 'hraInsight';
        this.disableSyncLists = !!data.definition?.disableSyncLists;

        if (data.definition?.uniqKey) {
            this.uniqKey = data.definition.uniqKey;
        }
    }

    get resilienceScore() {
        if (this._rawData && typeof this._rawData.resilienceScore === 'number') {
            return this._rawData.resilienceScore;
        }

        if (!Array.isArray(this.threatAreas) || this.threatAreas.length === 0) {
            return 0;
        }

        const withScores = this.threatAreas.filter(
            (a) => a && typeof a === 'object' && typeof a.score === 'number'
        );
        if (withScores.length === 0) return 0;

        const sum = withScores.reduce((acc, area) => acc + area.score, 0);
        return Math.round(sum / withScores.length);
    }


    setUserListSearchValue(userListSearchValue) {
        this.userListSearchValue = userListSearchValue;
    }

    setName = (value) => {
        this.name = value;
    };

    setActive = (value) => {
        this.active = value;
    }

    setId = (value) => {
        this.id = value;
    };

    toggleIntroMessage = () => {
        this.introMessageEnabled = !this.introMessageEnabled;
    };

    setScheduleType = (type) => {
        this.scheduleType = type;
    };

    setAuthentication = (type) => {
        this.authentication = type;
    }

    setChannel = (value, checked) => {
        let newChannel = this.channel;
        if (checked) {
            // add checked value to channel, this is called bitwise OR
            newChannel |= value;
        } else {
            // remove unchecked value from channel, this is called bitwise NOT
            newChannel &= ~value;
        }
        if (newChannel !== 0) {
            this.channel = newChannel;
            window.localStorage.setItem(
                `assessmentChannel-${this.companyId}`,
                this.channel
            );
            return true;
        } else {
            return false;
        }
    };

    setRemindersChannel = (value, checked) => {
        let newChannel = this.reminders_channel;
        if (checked) {
            // add checked value to channel, this is called bitwise OR
            newChannel |= value;
        } else {
            // remove unchecked value from channel, this is called bitwise NOT
            newChannel &= ~value;
        }
        if (newChannel !== 0) {
            this.reminders_channel = newChannel;
            window.localStorage.setItem(
                `assessmentRemindersChannel-${this.companyId}`,
                this.reminders_channel
            );
            return true;
        } else {
            return false;
        }
    };

    setDueDate = (date) => {
        this.due = date;
    };

    setDate = (date) => {
        this.date = date;
    };

    setDurationOverride = (duration) => {
        this.overrideDuration = duration;
    };

    setEmailsEnabled = () => {
        this.emails_enabled = !this.emails_enabled;
    };

    setCustomizationEnabled = () => {
        this.customization_enabled = !this.customization_enabled;
    };

    setBannerImageSrc = (src) => {
        this.bannerImageSrc = src;
    }

    setBannerLinkURL = (url) => {
        this.bannerLinkURL = url;
    }

    setFinishUrl = (url) => {
        this.finishUrl = url;
    }

    setRemindersEnabled = () => {
        if (!this.reminders_enabled && this.scheduleType === 0) {
            this.setReminder(
                moment(this.date).add(1, 'days').set('minute', 0),
                0
            );
        }
        this.reminders_enabled = !this.reminders_enabled;
    };

    setReminder = (date, i, reminder) => {
        if (reminder) {
            reminder.send = date;
        } else if (this.reminders_steps.length > 0) {
            this.reminders_steps[i].send = date;
        }
    };
    addReminderStep() {
        const last = this.reminders_steps[this.reminders_steps.length - 1];

        const nextDate = last?.send ? moment(last.send).add(1, 'day') : null;

        const inRange = nextDate && this.due && nextDate.isBefore(this.due);

        this.reminders_steps.push({
            id: this.nextReminderId++,
            send: inRange ? nextDate : null,
        });
    }

    deleteReminderStep(reminder) {
        const idx = this.reminders_steps.findIndex(r => r.id === reminder.id);
        if (idx !== -1) this.reminders_steps.splice(idx, 1);
    }

    toggleQuestionSelected(questionId) {
        if (this.selectedQuestionsIds.includes(questionId))
            this.selectedQuestionsIds.remove(questionId);
        else this.selectedQuestionsIds.push(questionId);
        this.templateCustomized = true;
    }

    toggleUserListSelected(listId) {
        if (this.selectedUserListsIds.includes(listId))
            this.selectedUserListsIds.remove(listId);
        else this.selectedUserListsIds.push(listId);
    }

    reorderQuestionsList(startIndex, endIndex) {
        if (endIndex < 0) {
            endIndex = 0;
        }
        const [removed] = this.selectedQuestionsIds.splice(startIndex, 1);
        this.selectedQuestionsIds.splice(endIndex, 0, removed);
    }

    get isUserCountOk() {
        let usersAvailable = 0;
        let usersRegistered = 0;

        if (this.company) {
            if (this.company.users_available) {
                usersAvailable = this.company.users_available;
            }
            usersRegistered = this.getCurrentCompanyUserCount;
        }
        return usersAvailable >= usersRegistered;
    }

    get statusInfo() {
        if (this.isScorm) {
            return {
                color: palette.graphite.heavyMetal,
                label: 'SCORM',
                transparent: true,
            };
        }
        return statuses[this.generalAssessmentStatus || this.status] || {};
    }

    get resilienceScoreDistribution() {
        return this._rawData.scoreDistribution.map((score) => ({
            ...score,
            employeePercent: (
                (score.value / this._rawData.usersFinished) *
                100
            ).toFixed(1),
        }));
    }

    get hasUnavailableQuestions() {
        return (
            this.unavailableQuestionsSelected &&
            this.unavailableQuestionsSelected.length > 0
        );
    }

    get link() {
        const assessmentLink = this.isAssessmentInsight ? '/assessment-insight' : '/assessment';
        if (this.status === STATUS.DRAFT) return `${assessmentLink}/edit/${this.id}`;
        return `${assessmentLink}/${this.isScorm ? 'p' : ''}${this.id}/view`;
    }

    get questionsSelected() {
        return this.selectedQuestionsIds
            .map((x) => this.questionsIndex[x])
            .filter((x) => x != null);
    }

    get unavailableQuestionsSelected() {
        return this.questionsSelected.filter((x) => x.disabled);
    }

    get maxByAreaAndBehaviourForSelectedQuestions() {
        let maxByAreaAndBehaviour = {};
        this.questionsSelected.forEach((q) => {
            Object.keys(q.maxScoresByBehaviour).forEach((b) => {
                if (!maxByAreaAndBehaviour[`${q.area}-${b}`])
                    maxByAreaAndBehaviour[`${q.area}-${b}`] = 0;
                maxByAreaAndBehaviour[`${q.area}-${b}`] +=
                    q.maxScoresByBehaviour[b];
            });
        });
        return maxByAreaAndBehaviour;
    }

    get userListsSuffix() {
        return this.selectedUserListsIds.length === 1
            ? ` - ${this.userListsSelected[0].name}`
            : '';
    }

    get userListsSelected() {
        return compact(
            this.selectedUserListsIds.map(
                (x) => this.listStore.allListsIndex[x]
            )
        );
    }

    get filteredUserListsSelected() {
        return this.userListsSelected.filter(
            (x) =>
                !(
                    this.userListSearchValue.length > 0 &&
                    x.name !== null &&
                    !x.name
                        .toLowerCase()
                        .includes(this.userListSearchValue.toLowerCase())
                )
        );
    }

    get selectedUserCount() {
        return sumBy(
            this.selectedUserListsIds.map(
                (x) => this.listStore.allListsIndex[x]
            ),
            'usersCount'
        );
    }

    get personalizeData() {
        let data = {};
        if (this.companyName) {
            data.companyName = this.companyName;
        }
        if (this.companyDomain) {
            data.companyDomain = this.companyDomain;
        }
        if (this.emailHR) {
            data.emailHR = this.emailHR;
        }
        return data;
    }

    //  get listNames() {
    //     if (this.listStore !== null) {
    //         return this.listStore.listsWithUsers.filter((list) =>
    //             (this._rawData.definition.userLists.includes(list.id))
    //         )
    //     }
    //     return null
    // }

    get canCopy() {
        if (!this.mainStore || !this.mainStore.isAdmin) return false;
        if (!this._rawData) return false;
        if (this.active) return false;
        if (this._rawData.events.every((x) => x.status > 0)) return false;

        return true;
    }

    get canRemind() {
        if (!this.mainStore || !this.mainStore.isAdmin) return false;
        if (!this._rawData || !this.active || this.isScorm) return false;
        return this.status === STATUS.ACTIVE;
    }

    get canEditReminders() {
        if (!this.mainStore || !this.mainStore.isAdmin) return false;
        if (!this._rawData || this.isScorm || this.isAssessmentInsight) {
            return false;
        }
        if (!this.active) return false;
        return (
            this.status === STATUS.ACTIVE || this.status === STATUS.SCHEDULED
        );
    }

    get isCompleted() {
        return this.status === STATUS.COMPLETED;
    }

    get questions() {
        return this._rawData.definition && this._rawData.definition.questions;
    }

    get personalizeEnabled() {
        return this.questionsSelected.find(
            (q) => 'personalizedKeysUsed' in q.definition
        ) !== undefined
            ? true
            : false;
    }

    get personalizedKeys() {
        let keys = [];
        this.personalizeEnabled &&
            this.questionsSelected.map((q) =>
                q.definition.personalizedKeysUsed !== undefined
                    ? q.definition.personalizedKeysUsed.map((k) => keys.push(k))
                    : null
            );
        return uniq(keys);
    }

    get deliveryDate() {
        return this._rawData.sendOptions.scheduleType === 1 || this.isAssessmentInsight
            ? this.status === STATUS.DRAFT
                ? null
                : this._rawData.created
            : this._rawData.sendOptions.date;
    }

    get completionDate() {
        return this._rawData.completed;
    }

    get userScore() {
        return this._rawData.score;
    }

    get summaryDeliveryDateText() {
        if (this.date !== null && this.scheduleType === 0)
            return moment(this.date).format(
                `L (LT) [${this.authStore.timeZone}]`
            );
        if (this.scheduleType === -1) return 'Manual';
        return 'Immediately';
    }

    get summaryAuthTypeText() {
        const authTypes = {
            'required-email': 'Required email',
            'no-email': 'No email',
            'optional-email': 'Optional email',
        }
        return authTypes[this.authentication] || 'No authentication';
    }

    get hasStarted() {
        return (
            (this.status === STATUS.ACTIVE ||
                this.status === STATUS.COMPLETED) &&
            this._rawData.usersFinished > 0
        );
    }

    get attendance() {
        return defaultZero(
            Math.round(
                (100 * (this._rawData.usersFinished || 0)) /
                    this._rawData.usersTotal
            )
        );
    }

    get selectedQuestionsDuration() {
        return (
            Object.values(this.questionsSelected).reduce(
                (q, { duration }) => q + duration,
                0
            ) / 60
        );
    }

    get recommendations() {
        return this._rawData.recommendations;
    }

    get areas() {
        return this._rawData.areas;
    }

    toDTO(extraData) {
        return {
            active: this.active,
            questions: this.selectedQuestionsIds,
            name: this.name.replace('&nbsp;', ' '),
            id: this.id,
            userLists: this.selectedUserListsIds,
            channel: this.channel,
            scheduleType: this.scheduleType,
            date: this.scheduleType === 0 ? this.date : null,
            timeOfDay: this.scheduleType === 0 ? moment(this.date).hours() : null,
            due: this.due ? this.due : null,
            reminders_enabled: this.reminders_enabled,
            reminders_settings: {
                channel: this.reminders_channel,
                steps: this.reminders_steps,
            },
            templateCustomized: this.templateCustomized,
            introMessage: this.introMessageEnabled,
            personalizeEnabled: this.personalizeEnabled,
            threatAreas: this.threatAreas,
            overrideDuration: this.overrideDuration,
            status: this.status,
            enabledLanguages: this.enabledLanguages,
            authentication: this.authentication,
            type: this.type,
            emails_enabled: this.emails_enabled,
            customization_enabled: this.customization_enabled,
            bannerImageSrc: this.bannerImageSrc || null,
            bannerLinkURL: this.bannerLinkURL || null,
            finishUrl: this.finishUrl || null,
            ...extraData,
        };
    }
}

export default Assessment;
